/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-vars */

/**
 *
 * UTILITY FUNCTIONS
 *
 */

WYATT = window.WYATT || {};

WYATT.debounce = function debounce(fn, wait) {
  let timerID;
  return (...args) => {
    clearTimeout(timerID);
    timerID = setTimeout(() => fn.apply(this, args), wait);
  };
};

WYATT.fetchConfig = function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
};

WYATT.asyncRequest = async function asyncRequest(url, options) {
  try {
    const response = await fetch(url, options);
    const data = await response.text();
    let error;

    if (response.ok) {
      return data;
    }

    if (data) {
      error = `${response.url} responded with: ${data}`;

      const { description, errors } = JSON.parse(data);
      const message = description || errors;

      if (message) {
        WYATT.createMessage("error", message);
      }
    } else {
      error = `${response.url} responded with status: ${response.status} and statusText: ${response.statusText}`;
    }

    throw new Error(error);
  } catch (error) {
    window.console.log(error);
    return false;
  }
};

WYATT.replaceDOM = function replaceDOM(
  html,
  selectorForExisting,
  selectorForNew = selectorForExisting
) {
  const newDOM = new DOMParser()
    .parseFromString(html, "text/html")
    .querySelector(selectorForNew);
  const existingDOM = document.querySelectorAll(selectorForExisting);

  if (newDOM && existingDOM) {
    existingDOM.forEach((element) => {
      element.outerHTML = newDOM.outerHTML;
    });
  }
};

WYATT.createMessage = function createMessage(
  type,
  message,
  appendTo = "#page-messages"
) {
  const pageMessageContainer = document.querySelector(appendTo);
  const template = document.getElementById("message");
  const clone = template.content.firstElementChild?.cloneNode(true);
  const typePlaceholder = clone.querySelector(
    "[data-message-type-placeholder]"
  );
  const textPlaceholder = clone.querySelector(
    "[data-message-text-placeholder]"
  );

  clone.classList.add(`message--${type}`);

  typePlaceholder.innerHTML = type;
  textPlaceholder.innerHTML = message;

  pageMessageContainer.appendChild(clone);
};

WYATT.waitForElement = function waitForElement(selector) {
  return new Promise((resolve) => {
    if (document.querySelector(selector)) {
      resolve(document.querySelector(selector));
      return;
    }

    const observer = new MutationObserver((mutations) => {
      if (document.querySelector(selector)) {
        resolve(document.querySelector(selector));
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  });
};

WYATT.selectedStore = function selectedStore() {
  return (
    JSON.parse(window.localStorage.getItem("storeLocation")) ||
    WYATT.shopify.locations[0]
  );
};

WYATT.registerComponent = function registerComponent(name, klass, options) {
  const exists = customElements.get(name);

  if (!exists) {
    customElements.define(name, klass, options);
  }
};

WYATT.cloneDOM = function cloneDOM(dom, prefix) {
  const clone = dom.cloneNode(true);
  const elementsWithID = clone.querySelectorAll("[id]");

  WYATT.config.duplicateIDCounter += 1;

  elementsWithID.forEach((element) => {
    const newId = `${prefix}-${WYATT.config.duplicateIDCounter}-${element.id}`;
    const label = clone.querySelector(`[for="${element.id}"]`);

    element.id = newId;
    label?.setAttribute("for", newId);
  });

  return clone;
};

WYATT.toggleScrollLock = function toggleScrollLock(addOrRemove) {
  document.querySelector("html").classList[addOrRemove]("scroll-lock");
};

WYATT.refreshYotpo = function refreshYotpo() {
  if (window.Yotpo === undefined) {
    return;
  }

  const api = new window.Yotpo.API(window.yotpo);
  api.refreshWidgets();
};

WYATT.sanitizedAddressFromCart = function sanitizedAddressFromCart() {
  let address = WYATT.shopify.cart.attributes.registry_address;

  if (address === undefined) {
    return null;
  }

  if (typeof address === "string") {
    address = address.replaceAll("=>", ":");
    address = JSON.parse(address);
  }

  if (WYATT.config.countryMapping[address.country]) {
    address.country = WYATT.config.countryMapping[address.country];
  }

  if (WYATT.config.provinceMapping[address.provice]) {
    address.province = WYATT.config.provinceMapping[address.province];
  }

  return JSON.stringify(address);
};

/**
 *
 * CUSTOM HTML ELEMENTS
 *
 */

class QuantityStepper extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });

    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  onButtonClick(event) {
    const previousValue = this.input.value;

    event.preventDefault();

    if (event.currentTarget.name === "plus") {
      this.input.stepUp();
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent);
    }
  }
}

customElements.define("quantity-stepper", QuantityStepper);

class LocalizationForm extends HTMLFormElement {
  constructor() {
    super();

    this.input = this.querySelector('input[name="locale_code"]');
    this.querySelectorAll("a").forEach((item) =>
      item.addEventListener("click", this.onItemClick.bind(this))
    );
  }

  onItemClick(event) {
    event.preventDefault();

    this.input.value = event.currentTarget.dataset.value;
    this.submit();
  }
}

customElements.define("localization-form", LocalizationForm, {
  extends: "form",
});

class ToggleClassButton extends HTMLButtonElement {
  constructor() {
    super();

    this.addEventListener("click", this.handleClick);
  }

  handleClick(event) {
    const dataID = this.dataset.toggleClassButton;
    const options = WYATT.config.customElements.toggleClassButton[dataID];
    const targetElement = document.querySelector(options.target);

    event.preventDefault();

    options.classNames.forEach((className) => {
      targetElement.classList.toggle(className);
    });

    if (options.focusElement) {
      targetElement.querySelector(options.focusElement).focus();
    }
  }
}

customElements.define("toggle-class-button", ToggleClassButton, {
  extends: "button",
});

class MessageDismissButton extends HTMLButtonElement {
  constructor() {
    super();

    this.addEventListener("click", this.handleClick);
  }

  handleClick(event) {
    const message = this.closest(".message");

    message.classList.add("message--removing");

    message.addEventListener("transitionend", () => message.remove());
  }
}

customElements.define("message-dismiss-button", MessageDismissButton, {
  extends: "button",
});

class TogglePasswordVisibilityButton extends HTMLButtonElement {
  constructor() {
    super();

    this.addEventListener("click", this.handleClick);
  }

  handleClick() {
    const password = this.previousElementSibling;

    if (password.type === "password") {
      password.type = "text";
    } else {
      password.type = "password";
    }
  }
}

customElements.define(
  "toggle-password-visibility-button",
  TogglePasswordVisibilityButton,
  {
    extends: "button",
  }
);

class FooterNavButton extends HTMLAnchorElement {
  constructor() {
    super();

    if (window.innerWidth <= WYATT.config.breakPoints.medium) {
      this.addEventListener("click", this.handleClick);
    }
  }

  handleClick(event) {
    event.preventDefault();

    this.closest(".footer-nav__item").classList.toggle(
      "footer-nav__item--active"
    );
  }
}

customElements.define("footer-nav-button", FooterNavButton, {
  extends: "a",
});

class ClearSearchButton extends HTMLButtonElement {
  constructor() {
    super();

    this.searchInput = this.form.querySelector('input[type="search"]');
    this.searchInput.addEventListener("input", this.handleInput.bind(this));
    this.addEventListener("click", this.handleClick);

    this.handleInput();
  }

  handleInput(event) {
    if (this.searchInput.value !== "") {
      this.removeAttribute("hidden");
      this.form.classList.add("has-value");
    } else {
      this.setAttribute("hidden", "");
      this.form.classList.remove("has-value");
    }
  }

  handleClick(event) {
    event.preventDefault();

    this.searchInput.value = "";
    this.searchInput.focus();
    this.setAttribute("hidden", "");
    this.form.classList.remove("has-value");
  }
}

customElements.define("clear-search-button", ClearSearchButton, {
  extends: "button",
});

class TabButton extends HTMLButtonElement {
  constructor() {
    super();

    if (this.getAttribute("aria-controls")) {
      this.tabs = this.closest("[data-tabs]");
      this.allTabs = [...this.tabs.querySelectorAll("[data-tab-button]")];

      this.addEventListener("mouseenter", this.handleHover);
      this.addEventListener("keydown", this.handleKeydown);
    } else {
      throw new Error(`#${this.id} is missing aria-controls attribute`);
    }
  }

  handleHover() {
    this.setSelectedTab(this);
  }

  handleKeydown(event) {
    switch (event.key) {
      case "ArrowUp":
        this.setSelectedToPreviousTab(event.currentTarget);
        break;

      case "ArrowDown":
        this.setSelectedToNextTab(event.currentTarget);
        break;

      case "Home":
        this.setSelectedTab(this.allTabs[0]);
        break;

      case "End":
        this.setSelectedTab(this.allTabs[this.allTabs.length - 1]);
        break;

      default:
        break;
    }
  }

  setSelectedToPreviousTab() {
    const firstTab = this.allTabs[0];
    const lastTab = this.allTabs[this.allTabs.length - 1];

    if (this === firstTab) {
      this.setSelectedTab(lastTab);
    } else {
      const index = this.allTabs.indexOf(this);

      this.setSelectedTab(this.allTabs[index - 1]);
    }
  }

  setSelectedToNextTab() {
    const firstTab = this.allTabs[0];
    const lastTab = this.allTabs[this.allTabs.length - 1];

    if (this === lastTab) {
      this.setSelectedTab(firstTab);
    } else {
      const index = this.allTabs.indexOf(this);

      this.setSelectedTab(this.allTabs[index + 1]);
    }
  }

  setSelectedTab(tabToSelect) {
    this.allTabs.forEach((tab) => {
      const panelID = tab.getAttribute("aria-controls");
      const panel = document.getElementById(panelID);

      if (tab === tabToSelect) {
        tab.setAttribute("aria-selected", "true");
        tab.removeAttribute("tabindex");
        tab.focus();
        tab.classList.add("selected");
        panel.classList.remove("hidden");
      } else {
        tab.setAttribute("aria-selected", "false");
        tab.setAttribute("tabindex", "-1");
        tab.classList.remove("selected");
        panel.classList.add("hidden");
      }
    });
  }
}

customElements.define("tab-button", TabButton, {
  extends: "button",
});

class ProductGrid extends HTMLElement {
  connectedCallback() {
    this.data = JSON.parse(this.dataset.productGrid);

    if (this.shouldGetContent()) {
      this.getGridContent();
    }
  }

  shouldGetContent() {
    const pageHasContent = Number.isInteger(this.data.contentPosition);

    if (!pageHasContent) {
      return false;
    }

    const paramString = window.location.search;
    const searchParams = new URLSearchParams(paramString);
    const currentPage = parseInt(searchParams.get("page"), 10);

    if (currentPage > 1) {
      return false;
    }

    return true;
  }

  getGridContent() {
    const endpoint = `${window.location.pathname}?view=grid-content`;

    fetch(endpoint)
      .then((response) => response.text())
      .then((html) => {
        this.injectGridContent(html);
      });
  }

  injectGridContent(html) {
    const content = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector("[data-grid-content]");
    const data = JSON.parse(content.dataset.gridContent);
    const item = this.buildGridItem(data.span);

    item.appendChild(content);

    WYATT.waitForElement(".product-grid__item").then(() => {
      const gridItems = this.querySelectorAll(".product-grid__item");

      this.querySelector(".product-grid").insertBefore(
        item,
        gridItems[data.position]
      );
    });
  }

  buildGridItem(span) {
    const item = document.createElement("li");

    item.classList.add(
      "product-grid__item",
      "product-grid__item--content",
      `product-grid__item--${span}`
    );

    return item;
  }
}

customElements.define("product-grid", ProductGrid);

/**
 * 1. Algolia does this weird thing where these product summary components keep getting appended instead of replaced.
 *    This was previously hacked around via CSS, hiding all but the first one.
 *    This was fine with their InstantSearch widget, but additional strange behavior was present when this component was integrated into the Autocomplete widget.
 *    Within the autocomplete, the every hover state change also triggered these components to fire.
 *    So even with the CSS hack, we were getting a ton of extra requests as well as a flash of content while the component is replaced.
 *    This checks for whether we even need to get a new product-summary html or if it's the same as already there.
 *    If it's the same, we just remove the custom element and return immediately.
 * 2. This gets rid of any remaining need of the css hack.
 */
class AlgoliaHit extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.handle;
    const endpoint = this.dataset.algoliaHit;

    if (this.dataset.handle === this.parentElement.dataset.handle) {
      /* [1] */
      this.remove();
      return;
    }

    this.cleanup(); /* [2] */

    if (endpoint) {
      this.classList.add("algolia-hit-is-loading");

      fetch(endpoint)
        .then((response) => response.text())
        .then((html) => {
          this.replaceHit(html);
          WYATT.refreshYotpo();
        });
    }
  }

  cleanup() {
    const children = Array.from(this.parentElement.children);

    children.forEach((el) => {
      if (el !== this) {
        el.remove();
      }
    });
  }

  replaceHit(html) {
    const newHit = new DOMParser().parseFromString(html, "text/html").body
      .childNodes[0];

    this.replaceWith(newHit);

    this.classList.remove("algolia-hit-is-loading");
    newHit.parentElement.dataset.handle = this.handle; /* [1] */
  }
}

customElements.define("algolia-hit", AlgoliaHit);

class CurrentFilters extends HTMLElement {
  connectedCallback() {
    const count = this.getCount();

    this.updateTotalCountDisplay(count);
  }

  updateTotalCountDisplay(count) {
    const display = document.querySelector("[data-total-filter-count-display]");

    display.textContent = count;
  }

  updateGroupCountDisplay(data) {
    const display = document.querySelector(
      `[data-filter-group-count-display="${data.attribute}"]`
    );
    const count = data.refinements.length;
    const countText = this.getCountText(count);

    display.textContent = countText;
  }

  clearGroupCountDisplays() {
    const displays = document.querySelectorAll(
      "[data-filter-group-count-display]"
    );

    displays.forEach((display) => {
      display.innerText = "";
    });
  }

  openGroup(data) {
    const toggle = document.getElementById(
      `filter-group-toggle-${data.attribute}`
    );

    if (data.refinements.length) {
      toggle.checked = true;
    }
  }

  getCount() {
    const filtersData = JSON.parse(this.dataset.currentFilters);
    let count = 0;

    if (filtersData.length) {
      filtersData.forEach((data) => {
        this.updateGroupCountDisplay(data);
        this.openGroup(data);

        count += data.refinements.length;
      });
    } else {
      this.clearGroupCountDisplays();
    }

    return count;
  }

  getCountText(count) {
    let text = WYATT.t.filters.filter;

    if (count > 1) {
      text = `${text}s`;
    }

    return `${count} ${text}`;
  }
}

customElements.define("current-filters", CurrentFilters);

class TruncateText extends HTMLElement {
  connectedCallback() {
    const content = this.querySelector("[data-truncate-text-content]");

    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const addOrRemove =
          content.scrollHeight > content.offsetHeight ? "remove" : "add";

        content.classList[addOrRemove]("truncate-text__content--no-truncation");
      });
    });

    observer.observe(content);
  }
}

customElements.define("truncate-text", TruncateText);

class WyattTabs extends HTMLElement {
  connectedCallback() {
    this.buttons = this.querySelectorAll("[data-tabs-button]");
    this.panels = this.querySelectorAll("[data-tabs-panel]");
    this.activeIndex = 0;

    this.buttons.forEach((button) => {
      button.addEventListener("click", this.handleClick.bind(this));
      button.addEventListener("keydown", this.handleKeydown.bind(this));
    });
  }

  handleClick(event) {
    event.preventDefault();

    this.setActiveTab(event.currentTarget);
  }

  handleKeydown(event) {
    const button = event.currentTarget;
    let flag = false;

    switch (event.key) {
      case "ArrowLeft":
        this.setActiveToPreviousTab();
        flag = true;
        break;

      case "ArrowRight":
        this.setActiveToNextTab();
        flag = true;
        break;

      case "Home":
        this.setActiveTab(this.buttons[0]);
        flag = true;
        break;

      case "End":
        this.setActiveTab(this.buttons[this.buttons.length - 1]);
        flag = true;
        break;

      default:
        break;
    }

    if (flag) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  setActiveToPreviousTab() {
    if (this.activeIndex === 0) {
      this.setActiveTab(this.buttons[this.buttons.length - 1]);
    } else {
      this.setActiveTab(this.buttons[this.activeIndex - 1]);
    }
  }

  setActiveToNextTab() {
    if (this.activeIndex === this.buttons.length - 1) {
      this.setActiveTab(this.buttons[0]);
    } else {
      this.setActiveTab(this.buttons[this.activeIndex + 1]);
    }
  }

  setActiveTab(buttonToActivate) {
    const newIndex = parseInt(buttonToActivate.dataset.tabsButton, 10);

    this.buttons.forEach((button, index) => {
      const shouldActivate = button === buttonToActivate;
      const panel = this.panels[index];

      if (shouldActivate) {
        button.setAttribute("aria-selected", "true");
        button.removeAttribute("tabindex");
        button.classList.add("tabs__button--active");
        button.focus();
        panel.classList.add("tabs__panel--active");
      } else {
        button.setAttribute("aria-selected", "false");
        button.tabIndex = -1;
        button.classList.remove("tabs__button--active");
        panel.classList.remove("tabs__panel--active");
      }
    });

    this.activeIndex = newIndex;
  }
}

customElements.define("wyatt-tabs", WyattTabs);

class WyattAccordion extends HTMLElement {
  connectedCallback() {
    this.buttons = this.querySelectorAll("[data-accordion-button]");

    this.buttons.forEach((button) => {
      button.addEventListener("click", this.handleClick.bind(this));
    });
  }

  handleClick(event) {
    const button = event.currentTarget;
    const isOpen = button.getAttribute("aria-expanded") === "true";

    this.toggle(button, isOpen);
  }

  toggle(button, isOpen) {
    const index = button.dataset.accordionButton;
    const panel = this.querySelector(`[data-accordion-panel="${index}"]`);

    if (isOpen) {
      button.classList.remove("accordion__button--active");
      button.setAttribute("aria-expanded", false);
      panel.classList.remove("accordion__panel--active");
    } else {
      button.classList.add("accordion__button--active");
      button.setAttribute("aria-expanded", true);
      panel.classList.add("accordion__panel--active");
    }
  }
}

customElements.define("wyatt-accordion", WyattAccordion);

class StoreLocation extends HTMLElement {
  connectedCallback() {
    this.store = JSON.parse(this.dataset.storeLocation);
    this.setStoreButton = this.querySelector("[data-set-location]");

    if (this.setStoreButton) {
      this.updateButtonDisplay();
      this.setStoreButton.addEventListener("click", this.setStore.bind(this));
    }

    if (this.classList.contains("location--simple")) {
      this.toggleStoreDisplay();
    }
  }

  toggleStoreDisplay() {
    const addOrRemove = this.isSelectedStore() ? "remove" : "add";

    this.classList[addOrRemove]("hidden");
  }

  isSelectedStore() {
    const selectedStoreID = WYATT.selectedStore().shopify_id;

    return selectedStoreID === this.store.shopify_id;
  }

  updateButtonDisplay() {
    if (this.isSelectedStore()) {
      this.setStoreButton.querySelector("span").textContent =
        WYATT.t.location.myStore;
      this.setStoreButton.classList.add("is-default-store");
    }
  }

  async setStore() {
    await this.setStoreCartAttributes();
    this.setStoreLocalStorage();

    window.location.reload();
  }

  async setStoreCartAttributes() {
    const body = {
      attributes: {
        selected_store: this.store,
      },
    };

    const options = {
      body: JSON.stringify(body),
      ...WYATT.fetchConfig(),
    };

    await WYATT.asyncRequest(WYATT.routes.cart_update_url, options);
  }

  setStoreLocalStorage() {
    window.localStorage.setItem("storeLocation", JSON.stringify(this.store));
  }
}

customElements.define("store-location", StoreLocation);

class SelectedStore extends HTMLElement {
  connectedCallback() {
    this.textContent = WYATT.selectedStore().name;
  }
}

customElements.define("selected-store", SelectedStore);

class BrowserTitle extends HTMLElement {
  connectedCallback() {
    document.title = this.textContent;
  }
}

customElements.define("browser-title", BrowserTitle);

class ReplaceWith extends HTMLElement {
  connectedCallback() {
    const selector = this.dataset.replaceWith;
    const target = document.querySelector(selector);

    if (target) {
      this.replaceWith(target);
      target.classList.remove("hidden");
    }
  }
}

customElements.define("replace-with", ReplaceWith);

class RegistryLink extends HTMLElement {
  constructor() {
    super();

    this.link = this.querySelector("a");

    WYATT.storingRegistryID = new Promise((resolve) => {
      this.resolvePromise = resolve;

      fetch(`${WYATT.routes.root}apps/giftregistry/manage`)
        .then((response) => response.text())
        .then(this.update.bind(this));
    });
  }

  update(html) {
    const DOM = new DOMParser().parseFromString(html, "text/html");
    const registries = DOM.querySelector("[data-registries]");
    const link = registries.querySelector(
      'a[href*="apps/giftregistry/registry"]'
    );

    if (!link) {
      this.resolvePromise();
      return;
    }

    this.link.href = link.href;
    WYATT.shopify.registry.id = link.href.split("/").pop();
    this.resolvePromise();
  }
}

customElements.define("registry-link", RegistryLink);

class ProductHotspot extends HTMLElement {
  connectedCallback() {
    this.config = {
      activeClass: "product-hotspot--active",
      mouseExitDelay: 400,
    };
    this.tooltip = this.querySelector("[data-product-hotspot-tooltip]");
    this.mouseEnterDelay = undefined;
    this.mouseExitDelay = undefined;
    this.active = false;
    this.positionTooltip();

    this.addEventListener("mouseenter", this.showTooltip.bind(this));
    this.addEventListener("focusin", this.showTooltip.bind(this));
    this.addEventListener("mouseleave", this.hideTooltip.bind(this));
    this.addEventListener("focusout", this.hideTooltip.bind(this));
  }

  showTooltip() {
    if (this.isActive) {
      clearTimeout(this.mouseExitDelay);
      return;
    }

    this.positionTooltip();
    this.classList.add(this.config.activeClass);
    this.isActive = true;
  }

  hideTooltip() {
    clearTimeout(this.mouseExitDelay);

    if (!this.isActive) {
      return;
    }

    this.mouseExitDelay = setTimeout(() => {
      this.classList.remove(this.config.activeClass);
      this.isActive = false;
    }, this.config.mouseExitDelay);
  }

  positionTooltip() {
    const {
      bottom: hotspotBottom,
      left: hotspotLeft,
      width: hotspotWidth,
    } = this.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const { width: tooltipWidth, height: tooltipHeight } =
      this.tooltip.getBoundingClientRect();
    const hotspotOffset = hotspotWidth / 2;
    const hotspotHorizontalCenter = hotspotLeft + hotspotOffset;

    if (hotspotBottom + tooltipHeight > window.innerHeight) {
      this.tooltip.style.top = "auto";
      this.tooltip.style.bottom = "120%";
    } else {
      this.tooltip.style.top = "100%";
      this.tooltip.style.bottom = "auto";
    }

    if (hotspotHorizontalCenter < tooltipWidth / 2) {
      this.tooltip.style.transform = "translateX(0)";
    } else if (hotspotHorizontalCenter + tooltipWidth / 2 > viewportWidth) {
      this.tooltip.style.transform = `translateX(calc(-100% + ${hotspotWidth}px))`;
    } else {
      this.tooltip.style.transform = `translateX(calc(-50% + ${hotspotOffset}px))`;
    }
  }
}

customElements.define("product-hotspot", ProductHotspot);
